import { useState } from 'react';
import { Button, Modal, ModalContent } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useGetStudentBadgesQuery, type IStudentBadge } from '@/entities/Badges';
import { Svg } from '@/shared/ui/Svg';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import CloseIcon from '@/shared/assets/icons/close.svg';
import confettiArt from '@/shared/assets/images/badge-confetti.svg?url';
import { readSeenAt, writeSeenAt } from '../lib/lastSeenBadge';
import { BadgeArt } from './BadgeArt';

interface Award {
  badge: IStudentBadge;
  earnedAt: number;
}

// Выдали несколько разом — поздравляем с последним
const latestAward = (items: IStudentBadge[]): Award | null =>
  items.reduce<Award | null>((latest, badge) => {
    if (!badge.isEarned || !badge.earnedAt) return latest;

    const earnedAt = Date.parse(badge.earnedAt);
    if (Number.isNaN(earnedAt)) return latest;

    return !latest || earnedAt > latest.earnedAt ? { badge, earnedAt } : latest;
  }, null);

const TILE_GRADIENT =
  'linear-gradient(114.9deg, rgba(33, 29, 85, 0.05) 2.63%, rgba(99, 0, 0, 0.05) 104.31%)';

// Сигнала о выдаче с сервера нет, поэтому сравниваем ответ с уже показанным earnedAt
export const BadgeCelebration = () => {
  const { t } = useTranslation();
  // Сервер о выдаче бейджа не сообщает — ни строкой уведомления, ни событием по сокету.
  // Поэтому список перезапрашивается сам: при монтировании макета, то есть на переходе
  // между разделами, и при возврате на вкладку. Иначе награду видно только после
  // полной перезагрузки страницы.
  const { data } = useGetStudentBadgesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const [seenAt, setSeenAt] = useState<number>(readSeenAt);

  const award = data ? latestAward(data.items) : null;

  useModalAutoFocus(Boolean(award) && (award?.earnedAt ?? 0) > seenAt);

  if (!award || award.earnedAt <= seenAt) return null;

  const { badge } = award;

  const close = () => {
    writeSeenAt(award.earnedAt);
    setSeenAt(award.earnedAt);
  };

  return (
    <Modal
      isOpen
      onOpenChange={close}
      hideCloseButton
      placement="center"
      motionProps={scaleInOut}
      classNames={{ base: 'w-[504px] max-w-full rounded-2xl p-9' }}
    >
      <ModalContent>
        <div className="flex w-full flex-col gap-5">
          <div className="flex w-full flex-col gap-6">
            <div className="flex w-full items-center justify-between">
              <p className="text-[30px] font-medium leading-[1.2] text-[#1a141f]">
                {t('badges.celebration.title')}
              </p>
              <button type="button" onClick={close} aria-label={t('badges.celebration.closeLabel')}>
                <Svg Icon={CloseIcon} className="text-[#1a141f]" />
              </button>
            </div>

            <div
              className="flex w-full items-center justify-center rounded-2xl"
              style={{ backgroundImage: TILE_GRADIENT }}
            >
              <div className="relative h-[200px] w-[210px]">
                <img
                  src={confettiArt}
                  alt=""
                  className="absolute inset-x-0 top-[3.59%] h-[87.53%] w-full"
                />
                <BadgeArt
                  badge={badge}
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                />
              </div>
            </div>

            <div className="flex w-full flex-col gap-2">
              <p className="text-center text-[40px] font-medium leading-[1.2] text-[#1a141f]">
                {badge.title}
              </p>
              <p className="text-center text-base leading-[1.4] text-[#1a141f]">
                {t('badges.celebration.description')}
              </p>
            </div>
          </div>

          <div className="flex w-full gap-5">
            <Button
              type="button"
              onPress={close}
              className="h-12 flex-1 rounded-full bg-[#211d55] px-4 py-2 text-base font-medium text-white transition-colors hover:bg-[#2a2568] active:bg-[#1a1744]"
            >
              {t('badges.celebration.closeButton')}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};
